import moment from 'moment';
import React from 'react';

import {CreateAnalysisParams} from 'analysis-tool/AnalysisDAO';
import {CreateAnalysisModal} from 'analysis-tool/create-analysis-modal/CreateAnalysisModal';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProcedureType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  isFeatureEnabledForProcedureType,
  ProceduresFeature,
} from 'procedures/utils/ProcedureCapabilityManagementHelpers';
import {ListableItem} from 'search/SearchConstants';
import {StudySearchItem} from 'studies/StudySearchInput';
import {createAnalysisForStudy} from 'studies/typed/utils/analysis/createAnalysisForStudy';
import ProcedureBasedStudyAnalysisModalBanner from 'studies/typed/utils/analysis/ProcedureBasedStudyAnalysisModalBanner';
import {getStudyTypeText} from 'studies/TypedStudyUtils';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import legacyRunModal from 'ui-library/legacyRunModal';
import APIUtil from 'util/APIUtil';
import * as DateUtil from 'util/DateUtil';

import {StudyListingGraphQL} from '../../../graphql-fragments/studies/__generated/StudyListingGraphQL';

/**
 * Study types that can be analyzed from the Studies listing page. Other study types (Clinical,
 * Preclinical, Custom) are not supported for cross-study analysis at this time.
 */
const CROSS_STUDY_ANALYSIS_SUPPORTED_STUDY_TYPES: ReadonlySet<StudyType> = new Set([
  StudyType.PROCESS_DEVELOPMENT,
  StudyType.ASSAY_DEVELOPMENT,
]);

export const NO_STUDIES_SELECTED_MESSAGE = 'Select one or more studies to analyze.';
export const MIXED_STUDY_TYPES_MESSAGE =
  'This button is disabled because you have selected multiple types of studies';
export function getTooManyStudiesMessage(limit: number): string {
  return `You can only analyze up to ${limit} studies at a time. Reduce your selection to continue.`;
}
export function getUnsupportedStudyTypeMessage(studyType: StudyType): string {
  return `${getStudyTypeText(studyType)} studies are not supported for analysis from this listing page.`;
}

/**
 * Return the selected `StudyListingGraphQL` items, given all items displayed in the listing and the ids
 * that the user has selected.
 */
export function getSelectedStudies(
  items: Array<ListableItem> | null,
  selectedIds: Array<string>
): Array<StudyListingGraphQL> {
  if (!items) {
    return [];
  }
  const selectedIdSet = new Set(selectedIds);
  const studies: Array<StudyListingGraphQL & ListableItem> = [];
  for (const item of items) {
    // Items in the Studies listing are always `StudyListingGraphQL` instances (the listing is configured
    // with `availableTypes: [ModelTypeConstants.STUDY]`); this id check is defensive.
    if (APIUtil.isStudy(item.id) && selectedIdSet.has(item.id)) {
      studies.push(item as StudyListingGraphQL & ListableItem);
    }
  }
  return studies;
}

/**
 * Whether the current user has permission to create cross-study analyses, based on their recipe/assay
 * capabilities. The user must have the `CREATE_ANALYSIS` feature for at least one of `RECIPE` or `ASSAY`.
 *
 * The `ASSAY` branch is additionally gated on `PROCEDURES_ENABLE_ASSAYS` so that tenants with the assay
 * capability but the assay-development feature flag off do not see the button.
 */
export function hasCreateAnalysisCapability(): boolean {
  return (
    isFeatureEnabledForProcedureType(ProcedureType.RECIPE, ProceduresFeature.CREATE_ANALYSIS) ||
    (BenchlingConfig.PROCEDURES_ENABLE_ASSAYS &&
      isFeatureEnabledForProcedureType(ProcedureType.ASSAY, ProceduresFeature.CREATE_ANALYSIS))
  );
}

/**
 * Returns the single study type selected by the user, or `null` if the user has selected studies of more
 * than one type. An empty selection also returns `null`.
 */
function getCommonStudyType(selectedStudies: Array<StudyListingGraphQL>): StudyType | null {
  if (selectedStudies.length === 0) {
    return null;
  }
  const studyTypes = new Set(selectedStudies.map((study) => study.schema.studyType));
  if (studyTypes.size !== 1) {
    return null;
  }
  return selectedStudies[0].schema.studyType;
}

/**
 * Returns the tooltip text to show when the Analyze button is disabled, or `null` when the selection is
 * valid and the button should be enabled.
 */
export function getDisabledTooltipText(
  selectedStudies: Array<StudyListingGraphQL>,
  studiesPerAnalysisLimit: number
): string | null {
  if (selectedStudies.length === 0) {
    return NO_STUDIES_SELECTED_MESSAGE;
  }
  if (selectedStudies.length > studiesPerAnalysisLimit) {
    return getTooManyStudiesMessage(studiesPerAnalysisLimit);
  }
  const commonStudyType = getCommonStudyType(selectedStudies);
  if (commonStudyType == null) {
    return MIXED_STUDY_TYPES_MESSAGE;
  }
  if (!CROSS_STUDY_ANALYSIS_SUPPORTED_STUDY_TYPES.has(commonStudyType)) {
    return getUnsupportedStudyTypeMessage(commonStudyType);
  }
  return null;
}

export interface CreateCrossStudyAnalysisButtonProps {
  selectedStudies: Array<StudyListingGraphQL>;
  actionOrigin: ActionOrigin;
}

function getInitialAnalysisNameForCrossStudyAnalysis(selectedStudies: Array<StudyListingGraphQL>): string {
  // format for analysis name: FirstStudyName_and_N_others_TIMESTAMP format
  assert(selectedStudies.length > 0, 'Expected at least one study to be selected.');
  const firstStudyName = selectedStudies[0].name;
  const numberOfOtherStudies = selectedStudies.length - 1;
  const analysisNamePrefix =
    numberOfOtherStudies > 0 ? `${firstStudyName}_and_${numberOfOtherStudies}_others` : firstStudyName;
  const sentToAnalysisTimestamp = DateUtil.readableTime(moment(), {showTime: true});
  return `${analysisNamePrefix}_${sentToAnalysisTimestamp}`;
}

export default function CreateCrossStudyAnalysisButton(
  props: CreateCrossStudyAnalysisButtonProps
): JSX.Element {
  const {selectedStudies, actionOrigin} = props;
  const tooltipText = getDisabledTooltipText(
    selectedStudies,
    BenchlingConfig.STUDIES_PER_CROSS_STUDY_ANALYSIS_LIMIT
  );
  const buttonDisabled = tooltipText != null;
  const initialStudySearchItems: Array<StudySearchItem> = selectedStudies.map((study) => ({
    id: study.id,
    name: study.name,
    humanId: study.humanId,
  }));
  const studyType = getCommonStudyType(selectedStudies);
  const title = selectedStudies.length > 1 ? 'Cross-study analysis' : 'Study analysis';

  const handleClick = async (): Promise<void> => {
    assert(studyType != null, 'Button should be disabled if no common study type is found.');
    await legacyRunModal(CreateAnalysisModal, {
      actionOrigin,
      onCreateAnalysis: (data: CreateAnalysisParams) =>
        createAnalysisForStudy(data, {actionOrigin, studyType}),
      initialStudySearchItems,
      disableEditStudies: true,
      title,
      initialAnalysisName: () => getInitialAnalysisNameForCrossStudyAnalysis(selectedStudies),
      headerInlineBanner: <ProcedureBasedStudyAnalysisModalBanner studyType={studyType} />,
      hideDataSharingWarningBanner: true,
      // TODO(BNCH-152505): Once we support MULTIPLE_STUDIES seed type, we can add it here.
      initialSeedDataConfig: undefined,
    });
  };

  return (
    <Tooltip text={tooltipText} isDisabled={tooltipText == null}>
      <Button
        dataTestAttrs={{component: 'CreateCrossStudyAnalysisButton', element: 'root'}}
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        size={ButtonSize.SMALL}
        onClick={handleClick}
        text="Analyze"
        icon={ModelTypeConstants.ANALYSIS}
        disabled={buttonDisabled}
      />
    </Tooltip>
  );
}
